import { html, nothing } from "lit";
import { property, state } from 'lit/decorators.js';
import { connect } from "pwa-helpers";
import store from "../../store/store";
import { StoreState } from "../../store/store.interface";
import { PaymentType, PaymentTypeMagento, PaymentTypeShop, RmsBookstoreAddress } from "../checkout/shop-checkout.interface";
import { BaseComponent } from "../../features/base-component/base-component";
import { booleanConverter } from "../../common/converters";
import { renderCompletedAddress } from "../../common/utils/payment-util";
import { getSelectedPaymentType, getShippingAddress, updateBillingAddress, updateBillingAddressSameAsShippingAddressFlag } from "../checkout/shop-checkout";
import { AddressForm } from "../address/address-form";

/**
 * Template to be implemented by every payment method.
 */
 export declare interface PaymentMethodTemplate {

  /**
   * Validate and update payment method details to redux.
   */
  validatePaymentMethod(): Promise<void>;

  /**
   * Method to be executed on edit payment method.
   */
  paymentMethodEditAction(): void;
}

/**
 * Available payment method options
 */
export class PaymentMethodType {
  static readonly PURCHASE_ORDER = new PaymentMethodType(PaymentType.PURCHASE_ORDER, 'Purchase Order', 1, 'mhecomm-po-order-payment-method');
  static readonly CREDIT_CARD = new PaymentMethodType(PaymentType.BRAINTREE_CREDIT_CARD, 'Credit Card', 2, 'mhecomm-credit-card-payment-method');
  static readonly PAYPAL = new PaymentMethodType(PaymentType.BRAINTREE_PAYPAL, 'Paypal', 3, 'mhecomm-paypal-payment-method');
  static readonly APPLE_PAY = new PaymentMethodType(PaymentType.BRAINTREE_APPLE_PAY, 'Apple Pay', 4, 'mhecomm-apple-pay-payment-method');
  static readonly GOOGLE_PAY = new PaymentMethodType(PaymentType.BRAINTREE_GOOGLE_PAY, 'Google Pay', 5, 'mhecomm-google-pay-payment-method');
  static readonly VENMO = new PaymentMethodType(PaymentType.BRAINTREE_VENMO, 'Venmo', 6, 'mhecomm-venmo-payment-method');
  static readonly IDEAL = new PaymentMethodType(PaymentType.BRAINTREE_IDEAL, 'iDEAL Pay', 7, 'mhecomm-ideal-payment-method');

  private constructor(
    public readonly type: PaymentType,
    public readonly displayName: string,
    public readonly order: number,
    public readonly component: string
  ) {}

  /**
   * Gets the mapped payment method type based on the payment type.
   *
   * @param paymentType
   */
  static getPaymentMethodType(paymentType: PaymentType): PaymentMethodType {
    switch (paymentType) {
      case PaymentType.BRAINTREE_APPLE_PAY:
        return PaymentMethodType.APPLE_PAY;
      case PaymentType.BRAINTREE_CREDIT_CARD:
        return PaymentMethodType.CREDIT_CARD;
      case PaymentType.BRAINTREE_GOOGLE_PAY:
        return PaymentMethodType.GOOGLE_PAY;
      case PaymentType.PURCHASE_ORDER:
        return PaymentMethodType.PURCHASE_ORDER;
      case PaymentType.BRAINTREE_PAYPAL:
        return PaymentMethodType.PAYPAL;
      case PaymentType.BRAINTREE_VENMO:
        return PaymentMethodType.VENMO;
      case PaymentType.BRAINTREE_IDEAL:
        return PaymentMethodType.IDEAL;
    }
  }

    /**
     * Gets the mapped payment method type based on the Order payment type.
     * It can be an order from magento or shop
     *
     * @param paymentType
     */
    static getPaymentMethodFromOrderPaymentType(paymentType: PaymentTypeMagento | PaymentTypeShop): PaymentMethodType {
      return {
        [PaymentTypeMagento.BRAINTREE_APPLE_PAY]: PaymentMethodType.APPLE_PAY,
        [PaymentTypeMagento.BRAINTREE_CREDIT_CARD]: PaymentMethodType.CREDIT_CARD,
        [PaymentTypeMagento.BRAINTREE_GOOGLE_PAY]: PaymentMethodType.GOOGLE_PAY,
        [PaymentTypeMagento.PURCHASE_ORDER]: PaymentMethodType.PURCHASE_ORDER,
        [PaymentTypeMagento.BRAINTREE_PAYPAL]: PaymentMethodType.PAYPAL,
        [PaymentTypeMagento.BRAINTREE_VENMO]: PaymentMethodType.VENMO,
        [PaymentTypeShop.BRAINTREE_CREDIT_CARD]: PaymentMethodType.CREDIT_CARD,
        [PaymentTypeShop.BRAINTREE_PAYPAL]: PaymentMethodType.PAYPAL,
        [PaymentTypeMagento.BRAINTREE_LOCAL_PAYMENT]: PaymentMethodType.IDEAL,
      }[paymentType];
    }
}

/**
 * Parent class of payment methods. Contains common billing address logic.
 */
export class PaymentMethod extends connect(store)(BaseComponent) {
  /**
   * Total payment amount.
   */
  @property({type: Number})
  totalAmount = 0;

  /**
   * Flag to indicate whether cart items are shippable.
   */
  @property({converter: booleanConverter})
  shippable = false;

  /**
   * Flag to indicate if the payment method is selected.
   */
  @state()
  protected isSelected = false;

  /**
   * Flag to indicate if billing address is same as shipping address. Update the payment checkout step context based on the cart state.
   */
  @state()
  protected isSameAsShippingAddress = false;

  @state()
  protected showUseShippingAddressButton = true;

  /**
   * Initial billing address state. Get back value from redux on state changed.
   */
  public billingAddressInitialState: RmsBookstoreAddress = {
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phoneNumber: '',
  }

  @state()
  protected billingAddress: RmsBookstoreAddress = this.billingAddressInitialState;

  validatePaymentMethod(): Promise<void> {
    throw new Error("Method not implemented.");
  }

  paymentMethodEditAction(): void {
    throw new Error("Method not implemented.");
  }

  /**
   * Indicates whether the payment method is selected.
   * If not, there is no point in rendering.
   *
   * @param paymentType
   * @param state
   */
  updateIsSelected(paymentType: PaymentType, state: StoreState): void {
    this.isSelected = getSelectedPaymentType(state) === paymentType;
  }

  /**
   * Render billing address form.
   * If cart items is shippable, render checkbox to copy shipping address to billing address. 
   * If 'use shipping address' checkbox is checked, display shipping address results as billing address.
   * If 'use shipping address' checkbox is unchecked, render billing address form.
   */
  protected renderBillingAddressForm(){
    return html`
      <h3 class="fw-semibold fs-4 mt-2 mb-0">Billing address</h3>
      <div>
        <small>Enter billing address as it appears on your debit or credit card.</small>
      </div>
      <div class="mt-2">
        ${this.shippable && this.showUseShippingAddressButton ? html`
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="sameShippingAddressCheckbox" name="sameShippingAddressCheckbox"
              @change="${this.useShippingAddressChangedEventHandler}" ?checked="${this.isSameAsShippingAddress}">
            <label for="sameShippingAddressCheckbox">Use shipping address</label>
          </div>`
          : nothing}
        ${this.isSameAsShippingAddress && this.billingAddress ? 
          html `
            <div class="pt-3">
            ${renderCompletedAddress(this.billingAddress)}
            </div>`
        : html`
          <div id="billing-address-container">
            <mhecomm-address-form 
              id="billing-address-form"
              type="billing"
              streetaddress="${this.billingAddress.address1}"
              city="${this.billingAddress.city}"
              countrystate="${this.billingAddress.state}"
              unit="${this.billingAddress.address2 || ""}"
              zipcode="${this.billingAddress.zipCode}"
              phonenum="${this.billingAddress.phoneNumber}">
            </mhecomm-address-form>
          </div>
        `}
      </div>
    `;
  }
  /**
   * 
   * @param event - when "same as shipping address" checkbox is clicked, copy shipping address to billing address.
   */
  protected useShippingAddressChangedEventHandler(event: InputEvent){
    const isSameAsShippingAddress = (event.target as HTMLInputElement).checked;
    this.useShippingAddressForBilling(isSameAsShippingAddress);
  }

  protected useShippingAddressForBilling(isSameAsShippingAddress: boolean) {
    if(isSameAsShippingAddress){
      store.dispatch(updateBillingAddress(getShippingAddress(store.getState() as StoreState)!));
    }
    store.dispatch(updateBillingAddressSameAsShippingAddressFlag(isSameAsShippingAddress));
    this.isSameAsShippingAddress = isSameAsShippingAddress;
  }

  /**
   * Get billing address form element.
   * @returns 
   */
  protected getAddressForm() {
    return this.shadowRoot!.querySelector("mhecomm-address-form") as AddressForm;
  }

}
